import React from 'react';
import Modal from '@amzn/awsui-components-core/modal';
import Button from '@amzn/awsui-components-core/button';
import Spinner from '@amzn/awsui-components-core/spinner';
import Alert from '@amzn/awsui-components-core/alert';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import type { StudioDataset } from 'src/components/star-grid/reportStudio.types';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import { useStudioLinePicker } from 'src/components/star-grid/hooks/useStudioLinePicker';
import { useStudioField } from 'src/components/star-grid/hooks/useStudioField';
import { isStudioBindingSelectable, studioDimensionBindings } from 'src/components/star-grid/utils/studioBindings';
import { expandLineSelection } from 'src/components/star-grid/utils/studioLineExpansion';
import { StudioSelect } from 'src/components/star-grid/components/StudioControls';
import MemberSubsetModal from 'src/components/star-grid/components/subset-editor/MemberSubsetModal';
import styles from 'src/components/star-grid/components/subset-editor/styles/MemberSubsetModal.module.scss';

interface Props {
  readonly dataset: StudioDataset;
  readonly bindingId: string;
  readonly members: readonly string[];
  readonly text: StarGridText;
  readonly onConfirm: (members: string[]) => void;
  readonly onDismiss: () => void;
  readonly testId?: string;
}
// Editing a line-set member row opens the same hierarchy picker filters use: a hierarchy dropdown
// over the dimension plus the member tree, with the row's current members preselected (mapped to
// tree positions by useStudioField, not by raw id). Multi-select folds into applyMembers upstream.
export const StudioLinePicker: React.FC<Props> = ({ dataset, bindingId, members, text, onConfirm, onDismiss, testId }) => {
  const picker = useStudioLinePicker(dataset, bindingId, members);
  const binding = picker.binding;
  const state = useStudioField(picker.field, binding!, () => undefined, false, true);
  const treeReady = Boolean(binding && state.choices.tree && state.choices.tree.hierarchy_id === (binding.tree_id ?? binding.id));
  if (!binding) return null;
  /*
   * Owned by the picker (it drives the binding and the tree load) but shown on the editor's toolbar row.
   * Rendered standalone only while the editor is down, so a hierarchy that fails to load can still be
   * swapped for another.
   */
  const hierarchySelect = (
    <StudioSelect
      inline
      label={text('studioHierarchy')}
      value={picker.bindingId}
      selectedLabel={binding.hierarchy}
      options={studioDimensionBindings(dataset, binding.id).map((item) => ({ id: item.id, label: item.hierarchy }))}
      onChange={picker.setBindingId}
    />
  );

  return (
    <Modal
      visible
      // The member editor renders EMBEDDED inside this modal, so THIS size — not the editor's own — is
      // what the tree and its search box get. `x-large` is the size the editor itself used when it was
      // still a standalone dialog; `large` (820px) cropped the member paths and squeezed the search box.
      size="x-large"
      data-testid={testId}
      // Titled as the editor titled ITSELF when it rendered its own dialog: embedded mode drops the
      // Modal `header` prop, so the host has to carry that text or the dialog loses it.
      header={`Select Elements - ${binding.label}`}
      closeAriaLabel={text('studioClose')}
      onDismiss={onDismiss}
      // `undefined`, not a falsy expression: a defined-but-falsy footer still reserves the Modal's footer
      // band, which read as dead space under the editor's own Cancel/OK.
      footer={!treeReady && !state.choices.loading ? <Button onClick={onDismiss}>{text('studioDone')}</Button> : undefined}
    >
      <SpaceBetween size="xs">
        {!treeReady && hierarchySelect}
        {!isStudioBindingSelectable(dataset, binding.id) && <Alert type="warning">{text('studioHierarchyUnavailable')}</Alert>}
        {state.choices.error && <Alert type="error">{state.choices.error.message}</Alert>}
        {/* A fixed-height slot for the editor. Switching hierarchy UNMOUNTS the tree while the next one
            loads, and without a reserved box the modal collapsed to its toolbar and sprang back — the
            spinner replaced 800px of content with one glyph. */}
        <div className={styles.pickerSlot}>
          {state.choices.loading && <Spinner />}
          {treeReady && (
            <MemberSubsetModal
              key={picker.bindingId}
              embedded
              visible
              hierarchyId={state.choices.tree!.hierarchy_id}
              hierarchyTree={state.choices.tree!}
              toolbarLeading={hierarchySelect}
              dimensionLabel={binding.label}
              multiple
              initialChecked={state.initialChecked}
              /* Always `self`: the member picker itself now offers member / children / leaves, so a second
               expansion control here was a duplicate of what the popup does. `self` was this control's
               default, so removing it changes nothing about what OK commits. */
              onConfirm={(picked) => onConfirm(expandLineSelection(state.choices.tree?.members ?? [], picked, 'self'))}
              onDismiss={onDismiss}
              testId={testId ? `${testId}-members` : undefined}
            />
          )}
        </div>
      </SpaceBetween>
    </Modal>
  );
};
